import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { ArrowRight, ShieldCheck, Globe2, ChevronLeft, ChevronRight } from 'lucide-react';
import { openInquiryPopup } from '../lib/contact';

const HERO_SLIDES = [
  {
    bg: 'https://images.unsplash.com/photo-1547448415-e9f5b28e570d?w=1920&q=80',
    badge: { icon: <ShieldCheck className="h-4 w-4" />, text: 'WHO Recognized Universities' },
    heading: (
      <>Your Journey to a <br/><span className="text-brand">Global Medical Career</span> <br/>Starts Here.</>
    ),
    sub: "Expert guidance for Indian students to study MBBS in top Russian government universities. Transparent admissions, guaranteed seats, and end-to-end support until you become a doctor.",
    cta1: { label: 'Explore MBBS in Russia', href: '/mbbs-in-russia' },
    cta2: { label: 'View Top Universities', href: '/universities' },
  },
  {
    bg: 'https://images.unsplash.com/photo-1520637836862-4d197d17c55a?w=1920&q=80',
    badge: { icon: <Globe2 className="h-4 w-4" />, text: '50+ Partner Universities' },
    heading: (
      <>Gateway to <br/><span className="text-brand">MBBS Global</span> <br/>Opportunities.</>
    ),
    sub: 'Open the door to a world-class medical degree. Join thousands of Indian students who chose Russia for affordable, WHO-recognised MBBS education — and never looked back.',
    cta1: { label: 'Start Your Application', href: 'https://wa.me/919509406802?text=Hi%2C%20I%20want%20to%20apply%20for%20MBBS%20in%20Russia.' },
    cta2: { label: 'Free Counselling', href: '/video-counselling' },
  },
];

export default function Hero() {
  const [i, setI] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setI(s => (s + 1) % HERO_SLIDES.length), 5500);
    return () => clearInterval(t);
  }, []);

  const s = HERO_SLIDES[i];

  return (
    <section className="relative min-h-[92vh] flex items-center pt-16 pb-40 overflow-hidden">
      {/* Bg */}
      <div key={i} className="absolute inset-0 z-0 animate-fade-up">
        <img src={s.bg} alt="Hero" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-primary/95 via-primary/80 to-primary/40" />
      </div>

      <div className="container relative z-10 mx-auto px-4 md:px-8 max-w-7xl">
        <div className="max-w-2xl text-white animate-fade-up">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/20 text-white font-medium text-sm mb-6 backdrop-blur-sm">
            {s.badge.icon}{s.badge.text}
          </div>
          <h1 className="font-serif text-5xl md:text-7xl font-bold leading-[1.05] mb-6">
            {s.heading}
          </h1>
          <p className="text-lg md:text-xl text-white/85 mb-8 leading-relaxed">{s.sub}</p>
          <div className="flex flex-col sm:flex-row gap-4">
            {s.cta1.href.startsWith('http') ? (
              <a href={s.cta1.href} target="_blank" rel="noopener noreferrer">
                <Button size="lg" className="bg-brand text-white hover:bg-brand-dark font-bold px-8 h-14 text-base rounded-full btn-shift">{s.cta1.label}</Button>
              </a>
            ) : (
              <Link to={s.cta1.href}>
                <Button size="lg" className="bg-brand text-white hover:bg-brand-dark font-bold px-8 h-14 text-base rounded-full btn-shift">{s.cta1.label}</Button>
              </Link>
            )}
            {s.cta2.label.toLowerCase().includes('free counsel') ? (
              <Button onClick={openInquiryPopup} data-testid="hero-cta2-popup" size="lg" variant="outline" className="border-white/40 bg-transparent text-white hover:bg-white hover:text-primary font-semibold h-14 px-8 rounded-full">{s.cta2.label}</Button>
            ) : (
              <Link to={s.cta2.href}>
                <Button size="lg" variant="outline" className="border-white/40 bg-transparent text-white hover:bg-white hover:text-primary font-semibold h-14 px-8 rounded-full">{s.cta2.label}</Button>
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Prev/Next */}
      <button onClick={() => setI(k => (k - 1 + HERO_SLIDES.length) % HERO_SLIDES.length)} aria-label="Previous"
        className="absolute left-4 top-1/2 -translate-y-1/2 z-20 h-10 w-10 rounded-full bg-white/20 border border-white/30 backdrop-blur-sm flex items-center justify-center hover:bg-white/40 transition text-white">
        <ChevronLeft className="h-5 w-5" />
      </button>
      <button onClick={() => setI(k => (k + 1) % HERO_SLIDES.length)} aria-label="Next"
        className="absolute right-4 top-1/2 -translate-y-1/2 z-20 h-10 w-10 rounded-full bg-white/20 border border-white/30 backdrop-blur-sm flex items-center justify-center hover:bg-white/40 transition text-white">
        <ChevronRight className="h-5 w-5" />
      </button>

      {/* Dots */}
      <div className="absolute bottom-28 left-1/2 -translate-x-1/2 z-20 flex gap-2">
        {HERO_SLIDES.map((_, k) => (
          <button key={k} onClick={() => setI(k)} aria-label={`Slide ${k+1}`}
            className={`rounded-full transition-all duration-300 ${k === i ? 'w-8 h-2.5 bg-white' : 'w-2.5 h-2.5 bg-white/40 hover:bg-white/70'}`} />
        ))}
      </div>
    </section>
  );
}
